// shared-lib/cabinet-runs/linkAnchors.js
//
// Stage 2 of resolveLayout for a run: the links that name this run's bays
// or ends become HARD ANCHORS before packing (reference-docs/
// layout-relationships §3.1).
//
//   center-on   one fixed member (opening / wall) + ≥1 appliance/bay member
//               on this run → each such bay's centre = the fixed param
//   frame       two bays of this run about a fixed axis → symmetric about it
//   align-end   this run's start / end → the target's param (opening edge,
//               wall start/end)
//
// A link that cannot be honoured — its fixed member is on another wall, it
// names two fixed members, its target kind is not resolvable here — is
// reported in `unsatisfied` with a reason and changes nothing. The link
// stays in the data; nothing here deletes intent. Pure.

const { canonicalType } = require('../group-customization/linkSemantics');
const { normalizeRef, isFixedRef } = require('../group-customization/refs');
const { anchorParamForRef, baysForRef } = require('./refAnchors');
const { worldToWallParam } = require('../kitchen-assembly/wallMath');
const { bayAt } = require('./bays');
const {
  RUN_ENDPOINT_SNAP_TOLERANCE,
} = require('../kitchen-assembly/constants');
const { ENDPOINT_SNAP } = require('../kitchen-assembly/intentRules');

const IN = 0.0254;
const MIN_FRAME_GAP = 3 * IN;

function segIndexOfBay(bay) {
  return bay.segmentIndex ?? 0;
}

function setBayAnchors(run, updates) {
  if (!updates.size) return run;
  return {
    ...run,
    // `bayAt`: a vanity sink bay's bowl seat moves with it.
    bays: (run.bays || []).map((b) =>
      updates.has(b.id) ? bayAt(b, updates.get(b.id)) : b
    ),
  };
}

/**
 * @param {Object} run — rehydrated, with `bays` (use `baysForRun` first)
 * @param {Object[]} links — the room-level links naming this run
 * @param {{ openings?, walls? }} ctx
 * @returns {{ run, applied: Object[], unsatisfied: Object[] }}
 */
function applyLinkAnchors(run, links, ctx = {}) {
  const applied = [];
  const unsatisfied = [];
  const bayUpdates = new Map();
  let segments = run.segments || [];

  for (const link of links || []) {
    if (!link) continue;
    const t = canonicalType(link.type);
    const refuse = (reason, extra = {}) =>
      unsatisfied.push({ linkId: link.id, reason, ...extra });

    if (t === 'center-on') {
      const members = [
        ...(link.memberIds || []).map((id) => ({ kind: 'object', id })),
        ...(link.fixedMembers || []).map(normalizeRef),
      ];
      const fixed = members.filter(isFixedRef);
      const bayRefs = members.filter(
        (m) => m.kind === 'appliance' || m.kind === 'bay'
      );
      const bays = bayRefs.flatMap((m) => baysForRef(m, run));
      if (!bays.length) continue; // not about this run
      if (fixed.length > 1) {
        refuse('over-constrained');
        continue;
      }
      if (fixed.length === 0) {
        // appliance ↔ appliance with no fixed node: same centre — the first
        // named bay leads. (Two appliances on one run sharing a centre is
        // rare; keep it honest rather than clever.)
        const lead = bays[0].anchorParam;
        for (const b of bays.slice(1)) bayUpdates.set(b.id, lead);
        applied.push({ linkId: link.id, bayId: bays[0].id, param: lead });
        continue;
      }
      for (const bay of bays) {
        const p = anchorParamForRef(fixed[0], {
          run,
          segIndex: segIndexOfBay(bay),
          openings: ctx.openings,
          walls: ctx.walls,
        });
        if (p == null) {
          refuse('off-wall', { bayId: bay.id });
          continue;
        }
        bayUpdates.set(bay.id, p);
        applied.push({ linkId: link.id, bayId: bay.id, param: p });
      }
      continue;
    }

    if (t === 'frame') {
      if (!link.axis) continue; // free-axis frames are the group solver's
      const [a] = baysForRef(link.objectAId, run);
      const [b] = baysForRef(link.objectBId, run);
      if (!a && !b) continue;
      if (!a || !b || segIndexOfBay(a) !== segIndexOfBay(b)) {
        refuse('off-wall');
        continue;
      }
      const axis = anchorParamForRef(link.axis, {
        run,
        segIndex: segIndexOfBay(a),
        openings: ctx.openings,
        walls: ctx.walls,
      });
      if (axis == null) {
        refuse('off-wall');
        continue;
      }
      const half = Math.max(
        Math.abs(a.anchorParam - b.anchorParam) / 2,
        (a.width + b.width) / 2 + MIN_FRAME_GAP
      );
      const [left, right] = a.anchorParam <= b.anchorParam ? [a, b] : [b, a];
      bayUpdates.set(left.id, axis - half);
      bayUpdates.set(right.id, axis + half);
      applied.push({ linkId: link.id, bayId: left.id, param: axis - half });
      applied.push({ linkId: link.id, bayId: right.id, param: axis + half });
      continue;
    }

    if (t === 'align-end') {
      const re = normalizeRef(link.runEnd);
      if (!re || re.runId !== run.id) continue;
      const target = normalizeRef(link.target);
      const segIndex = re.end === 'start' ? 0 : segments.length - 1;
      if (segIndex < 0 || !segments[segIndex]) {
        refuse('off-wall');
        continue;
      }
      let p = null;
      if (target.kind === 'opening' || target.kind === 'wall') {
        p = anchorParamForRef(target, {
          run,
          segIndex,
          openings: ctx.openings,
          walls: ctx.walls,
        });
      } else if (target.kind === 'run-end') {
        // Another run's terminal edge (the two arms of a U squaring their
        // mouth): its world point, projected onto THIS segment's wall.
        // `ctx.runEnds` — runId → { start: {x,z}, end: {x,z} } — comes from
        // the caller, which knows every run in the room.
        const other = ctx.runEnds?.[target.runId]?.[target.end];
        const wall = segments[segIndex].wall;
        p = other && wall ? worldToWallParam(wall, other) : null;
      } else {
        refuse('unsupported'); // object edges: not yet
        continue;
      }
      if (p == null) {
        refuse('off-wall');
        continue;
      }
      // A link the generator's endpoint snap wrote records a NUDGE: that
      // pass moves a run's terminal edge onto an anchor it is already within
      // `RUN_ENDPOINT_SNAP_TOLERANCE` (2") of, and never further. Applied as
      // the snap meant it, the link moves this end by at most that; asked for
      // more, it is describing an edge that is not this run's end — the
      // generator's cabinet row started past a fridge, and the stored run
      // starts AT the fridge — and setting the end outright hauled the fridge
      // a full width on the first edit (`runFillInvariants.nightly`,
      // 2026-10-20; the 64" haul of #2176 was this with a stale link). It is
      // refused and reported, like every other link that cannot be honoured.
      const current =
        re.end === 'start'
          ? segments[segIndex].startParam
          : segments[segIndex].endParam;
      if (
        link.rule === ENDPOINT_SNAP &&
        Number.isFinite(current) &&
        Math.abs(p - current) > RUN_ENDPOINT_SNAP_TOLERANCE
      ) {
        refuse('too-far', { delta: p - current });
        continue;
      }
      segments = segments.map((s, i) =>
        i !== segIndex
          ? s
          : re.end === 'start'
            ? { ...s, startParam: p }
            : { ...s, endParam: p }
      );
      applied.push({ linkId: link.id, end: re.end, param: p });
      continue;
    }
  }

  const next = setBayAnchors({ ...run, segments }, bayUpdates);
  return { run: next, applied, unsatisfied };
}

module.exports = { applyLinkAnchors };
